import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft, ShieldCheck, Zap, Headphones, Coins, LineChart, Wallet, ArrowLeftRight,
  BadgeCheck, Lock, Smartphone, TrendingUp, TrendingDown, Flame, Sparkles,
} from 'lucide-react'
import { Shell } from '../components/Layout'
import { Card, SectionHead, Tabs, Counter } from '../components/UI'
import { CoinIcon, MarketTable, Sparkline, Ticker } from '../components/Market'
import { useMarket, useTickers } from '../store'
import { get } from '../lib/api'
import { clsx, compact, fa, num, pct, changeClass, asArray} from '../lib/format'
import { isNativeApp } from '../lib/native'
import { useSeo } from '../lib/seo'

const FEATURES = [
  { icon: Zap, title: 'معامله در کمتر از ۲ ثانیه', text: 'موتور معاملاتی ارزینو با ظرفیت ۱۰۰ هزار سفارش در ثانیه، سفارش شما را بدون تأخیر اجرا می‌کند.', color: '#7C5CFF' },
  { icon: ShieldCheck, title: 'امنیت در سطح بانکی', text: 'نگهداری ۹۵٪ دارایی‌ها در کیف پول سرد چندامضایی، ورود دومرحله‌ای و رمزنگاری کامل داده‌ها.', color: '#22D3EE' },
  { icon: Coins, title: 'کمترین کارمزد بازار', text: 'کارمزد از ۰٫۰۲٪ شروع می‌شود و با افزایش حجم معاملات ماهانه به‌صورت خودکار کاهش می‌یابد.', color: '#00E39A' },
  { icon: Headphones, title: 'پشتیبانی شبانه‌روزی', text: 'تیم پشتیبانی ارزینو در تمام ساعات شبانه‌روز، حتی روزهای تعطیل، در کنار شماست.', color: '#FFB020' },
  { icon: Wallet, title: 'واریز و برداشت آنی ریالی', text: 'شارژ کیف پول با درگاه مستقیم شتاب و تسویه برداشت‌ها در سریع‌ترین زمان ممکن.', color: '#FF4D6D' },
  { icon: LineChart, title: 'ابزار حرفه‌ای تحلیل', text: 'نمودار پیشرفته، دفتر سفارشات زنده، انواع سفارش limit، market و stop-limit.', color: '#9A82FF' },
]

const STEPS = [
  { n: '۱', title: 'ثبت‌نام کنید', text: 'تنها با ایمیل و در کمتر از یک دقیقه حساب خود را بسازید.', icon: BadgeCheck },
  { n: '۲', title: 'احراز هویت', text: 'اطلاعات هویتی خود را وارد کنید تا حساب شما فعال شود.', icon: ShieldCheck },
  { n: '۳', title: 'شارژ کیف پول', text: 'با درگاه بانکی یا واریز رمزارز کیف پول خود را شارژ کنید.', icon: Wallet },
  { n: '۴', title: 'شروع معامله', text: 'در بازار سریع یا حرفه‌ای معامله کنید و سود بگیرید.', icon: ArrowLeftRight },
]

function HeroCoin({ symbol, className }: { symbol: string; className?: string }) {
  const t = useMarket((s) => s.tickers[symbol])
  if (!t) return null
  return (
    <div className={clsx('glass-strong absolute hidden rounded-2xl p-3.5 shadow-card lg:block', className)}>
      <div className="flex items-center gap-3">
        <CoinIcon symbol={t.base} color={t.color} size={36} />
        <div>
          <div className="text-xs text-slate-400">{t.faName}</div>
          <div className="num text-sm font-bold text-white">{num(t.last)}</div>
        </div>
        <div className={clsx('num mr-2 text-xs font-semibold', changeClass(t.change))}>{pct(t.change)}</div>
      </div>
    </div>
  )
}

export default function Home() {
  useSeo({
    title: 'صرافی ارز دیجیتال | خرید و فروش بیت‌کوین و تتر',
    description: 'ارزینو؛ خرید و فروش امن و سریع بیت‌کوین، تتر و بیش از ۲۰ ارز دیجیتال با کمترین کارمزد، تسویه آنی ریالی و پشتیبانی شبانه‌روزی.',
    keywords: 'صرافی ارز دیجیتال، خرید بیت کوین، خرید تتر، قیمت ارز دیجیتال',
    canonical: '/',
  })
  const irtRows = useTickers('IRT')
  const usdt = useMarket((s) => s.tickers['USDTIRT'])
  const [tab, setTab] = useState('hot')
  const [summary, setSummary] = useState<any>(null)
  const [posts, setPosts] = useState<any[]>([])

  useEffect(() => {
    get('/market/summary').then(setSummary).catch(() => {})
    get('/content/posts?limit=3').then((r: any) => setPosts(asArray(r))).catch(() => {})
  }, [])

  const irt = irtRows
  const rows =
    tab === 'gainers' ? [...irt].sort((a, b) => b.change - a.change).slice(0, 8)
    : tab === 'losers' ? [...irt].sort((a, b) => a.change - b.change).slice(0, 8)
    : [...irt].sort((a, b) => b.quoteVolume - a.quoteVolume).slice(0, 8)

  return (
    <Shell full>
      {/* ------------------------------------------------------------ هیرو */}
      <section className="noise relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-grid-fade bg-grid opacity-40 [mask-image:radial-gradient(ellipse_at_center,black,transparent_72%)]" />
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 animate-float rounded-full bg-brand-500/25 blur-[110px]" />
        <div className="pointer-events-none absolute -left-32 top-40 h-80 w-80 animate-float rounded-full bg-aqua/20 blur-[110px] [animation-delay:2s]" />

        <div className="relative mx-auto max-w-[1400px] px-4 pb-10 pt-8 sm:px-6 sm:pb-16 sm:pt-20">
          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr]">
            <div>
              <span className="chip mb-5 border-brand-500/30 bg-brand-500/10 text-brand-200">
                <Sparkles className="h-3.5 w-3.5" />
                بیش از {fa('۴۲۸')} هزار کاربر فعال به ارزینو اعتماد کرده‌اند
              </span>
              <h1 className="text-[1.75rem] font-black leading-[1.3] text-white xs:text-4xl sm:text-5xl lg:text-6xl lg:leading-[1.2]">
                بازار رمزارز را
                <span className="bg-gradient-to-l from-brand-400 via-brand-200 to-aqua bg-clip-text text-transparent"> ساده </span>
                تجربه کنید
              </h1>
              <p className="mt-6 max-w-xl text-base leading-8 text-slate-400 sm:text-lg">
                خرید و فروش بیت‌کوین، تتر و بیش از {fa('۲۰')} ارز دیجیتال با کمترین کارمزد،
                تسویه آنی ریالی و ابزارهای حرفه‌ای تحلیل — همه در یک پلتفرم.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" className="btn-primary !px-6 !py-3.5 text-base">
                  رایگان شروع کنید <ArrowLeft className="h-4 w-4" />
                </Link>
                <Link to="/markets" className="btn-ghost !px-6 !py-3.5 text-base">مشاهده قیمت‌ها</Link>
              </div>

              <div className="mt-10 grid max-w-lg grid-cols-3 gap-4">
                {[
                  ['کاربران فعال', summary?.stats?.users ?? 428500, ''],
                  ['حجم معاملات ۲۴ ساعت', summary?.stats?.volume24 ?? 0, 'compact'],
                  ['آپ‌تایم سرویس', 99.98, 'pct'],
                ].map(([label, value, kind]: any) => (
                  <div key={label}>
                    <div className="num text-xl font-extrabold text-white sm:text-2xl">
                      {kind === 'compact' ? compact(value) : kind === 'pct' ? `${fa(value)}٪` : <Counter value={value} />}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* کارت خرید سریع */}
            <div className="relative">
              <HeroCoin symbol="BTCIRT" className="-right-4 -top-6 z-10" />
              <HeroCoin symbol="ETHIRT" className="-left-8 bottom-8 z-10 [animation-delay:1.5s]" />
              <div className="glass-strong relative rounded-3xl p-6 shadow-card">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-bold text-white">خرید سریع</h3>
                  <span className="chip text-[11px] text-slate-400">
                    نرخ تتر: <span className="num text-up">{num(usdt?.last)}</span>
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="rounded-2xl border border-white/[.07] bg-ink-900/60 p-4">
                    <div className="mb-2 text-xs text-slate-500">پرداخت می‌کنید</div>
                    <div className="flex items-center justify-between">
                      <span className="num text-2xl font-bold text-white">{fa('۱۰,۰۰۰,۰۰۰')}</span>
                      <span className="chip">تومان</span>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-ink-800">
                      <ArrowLeftRight className="h-4 w-4 text-brand-300" />
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/[.07] bg-ink-900/60 p-4">
                    <div className="mb-2 text-xs text-slate-500">دریافت می‌کنید (تقریبی)</div>
                    <div className="flex items-center justify-between">
                      <span className="num text-2xl font-bold text-up">
                        {usdt ? num(10_000_000 / usdt.last, 2) : '—'}
                      </span>
                      <span className="chip">تتر</span>
                    </div>
                  </div>
                </div>
                <Link to="/convert" className="btn-primary mt-5 w-full !py-3.5">همین حالا بخرید</Link>
                <div className="mt-4 flex items-center justify-center gap-4 text-[11px] text-slate-500">
                  <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> تسویه آنی</span>
                  <span className="flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> بدون کارمزد پنهان</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Ticker rows={irtRows} />

      {/* ------------------------------------------------------------ بازارها */}
      <section className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-16">
        <SectionHead
          title="بازارهای ارزینو"
          subtitle="قیمت لحظه‌ای ارزهای دیجیتال بر حسب تومان"
          action={<Link to="/markets" className="btn-ghost text-sm">همه بازارها <ArrowLeft className="h-4 w-4" /></Link>}
        />
        <Card className="!p-0">
          <div className="flex items-center justify-between gap-3 border-b border-white/[.06] p-4">
            <Tabs
              value={tab}
              onChange={setTab}
              size="sm"
              items={[
                { key: 'hot', label: <span className="flex items-center gap-1"><Flame className="h-3.5 w-3.5" />پرمعامله</span> },
                { key: 'gainers', label: <span className="flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" />بیشترین رشد</span> },
                { key: 'losers', label: <span className="flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5" />بیشترین افت</span> },
              ]}
            />
          </div>
          <div className="p-2">
            <MarketTable rows={rows} />
          </div>
        </Card>
      </section>

      {/* ------------------------------------------------------------ ویژگی‌ها */}
      <section className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-16">
        <SectionHead title="چرا ارزینو؟" subtitle="هر آنچه یک معامله‌گر حرفه‌ای نیاز دارد، در یک پلتفرم" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <Card key={f.title} hover className="group relative overflow-hidden">
              <div
                className="absolute -left-10 -top-10 h-28 w-28 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-60"
                style={{ background: f.color }}
              />
              <div className="relative">
                <div
                  className="mb-4 grid h-12 w-12 place-items-center rounded-2xl"
                  style={{ background: `${f.color}1F`, border: `1px solid ${f.color}44`, color: f.color }}
                >
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className="mb-2 font-bold text-white">{f.title}</h3>
                <p className="text-sm leading-7 text-slate-400">{f.text}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------ مراحل */}
      <section className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-16">
        <SectionHead title="در ۴ قدم شروع کنید" subtitle="از ثبت‌نام تا اولین معامله، کمتر از ده دقیقه" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <Card key={s.n} hover className="relative">
              <span className="num absolute left-4 top-4 text-4xl font-black text-white/[.06]">{s.n}</span>
              <div className="mb-4 grid h-11 w-11 place-items-center rounded-xl border border-brand-500/30 bg-brand-500/10 text-brand-300">
                <s.icon className="h-5 w-5" />
              </div>
              <h3 className="mb-2 font-bold text-white">{s.title}</h3>
              <p className="text-sm leading-7 text-slate-400">{s.text}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------ استیکینگ + امنیت */}
      <section className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-16">
        <div className="grid gap-5 lg:grid-cols-2">
          <Card className="relative overflow-hidden !p-8">
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-up/20 blur-[80px]" />
            <div className="relative">
              <span className="chip mb-4 border-up/30 bg-up/10 text-up"><Coins className="h-3.5 w-3.5" /> درآمد غیرفعال</span>
              <h3 className="mb-3 text-2xl font-extrabold text-white">تا {fa('۳۲')}٪ سود سالانه با استیکینگ</h3>
              <p className="mb-6 max-w-md text-sm leading-7 text-slate-400">
                دارایی خود را در طرح‌های زمان‌دار ارزینو قفل کنید و سود روزانه دریافت کنید.
                هر زمان بخواهید می‌توانید اصل سرمایه را آزاد کنید.
              </p>
              <Link to="/staking" className="btn-up">مشاهده طرح‌ها <ArrowLeft className="h-4 w-4" /></Link>
            </div>
          </Card>

          <Card className="relative overflow-hidden !p-8">
            <div className="pointer-events-none absolute -left-16 -top-16 h-56 w-56 rounded-full bg-aqua/20 blur-[80px]" />
            <div className="relative">
              <span className="chip mb-4 border-aqua/30 bg-aqua/10 text-aqua"><ShieldCheck className="h-3.5 w-3.5" /> امنیت</span>
              <h3 className="mb-3 text-2xl font-extrabold text-white">دارایی شما، امن‌تر از همیشه</h3>
              <ul className="mb-6 space-y-3 text-sm text-slate-400">
                {['کیف پول سرد چندامضایی برای ۹۵٪ دارایی‌ها',
                  'ورود دومرحله‌ای و تأیید برداشت با کد یکبارمصرف',
                  'مانیتورینگ ۲۴ ساعته و سیستم تشخیص تقلب',
                  'رمزنگاری کامل اطلاعات کاربران'].map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-aqua" />{t}
                  </li>
                ))}
              </ul>
              <Link to="/security" className="btn-aqua">تنظیمات امنیتی <ArrowLeft className="h-4 w-4" /></Link>
            </div>
          </Card>
        </div>
      </section>

      {/* ------------------------------------------------------------ بلاگ */}
      {posts.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-16">
          <SectionHead
            title="آموزش و اخبار"
            subtitle="تازه‌ترین تحلیل‌ها و آموزش‌های تیم ارزینو"
            action={<Link to="/blog" className="btn-ghost text-sm">همه مطالب <ArrowLeft className="h-4 w-4" /></Link>}
          />
          <div className="grid gap-5 md:grid-cols-3">
            {posts.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`}>
                <Card hover className="h-full">
                  <div className="mb-4 h-32 rounded-xl bg-gradient-to-br from-brand-600/40 via-brand-500/10 to-aqua/25" />
                  <span className="chip mb-3 text-[11px] text-brand-200">{p.category}</span>
                  <h3 className="mb-2 font-bold leading-7 text-white">{p.title}</h3>
                  <p className="text-sm leading-7 text-slate-400">{p.excerpt}</p>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* --------------------------------------------------- اپ اندروید
          فقط وقتی نشان داده می‌شود که واقعاً منتشر شده باشد (تنظیمات مدیر)،
          و در خودِ اپ اصلاً دیده نمی‌شود — دعوت به دانلود چیزی که کاربر
          هم‌اکنون داخلش است، بی‌معنی است. */}
      <AndroidBanner />

      {/* ------------------------------------------------------------ CTA */}
      <section className="mx-auto max-w-[1400px] px-4 pb-8 sm:px-6">
        <div className="glass-strong relative overflow-hidden rounded-3xl p-10 text-center">
          <div className="pointer-events-none absolute inset-0 bg-grid-fade bg-grid opacity-30" />
          <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-[36rem] -translate-x-1/2 rounded-full bg-brand-500/25 blur-[100px]" />
          <div className="relative">
            <Smartphone className="mx-auto mb-4 h-10 w-10 text-brand-300" />
            <h2 className="mb-3 text-2xl font-extrabold text-white sm:text-3xl">همین امروز معامله را شروع کنید</h2>
            <p className="mx-auto mb-7 max-w-xl text-sm leading-7 text-slate-400">
              ثبت‌نام رایگان است و کمتر از یک دقیقه زمان می‌برد. پس از احراز هویت می‌توانید بلافاصله معامله کنید.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/register" className="btn-primary !px-7 !py-3.5">ایجاد حساب کاربری</Link>
              <Link to="/faq" className="btn-ghost !px-7 !py-3.5">سؤالات متداول</Link>
            </div>
          </div>
        </div>
      </section>
    </Shell>
  )
}

/* ═══════════════════════════════════ نوار دانلود اپ اندروید */
function AndroidBanner() {
  const [app, setApp] = useState<any>(null)

  useEffect(() => {
    if (isNativeApp()) return
    get('/app/version').then(setApp).catch(() => setApp(null))
  }, [])

  if (isNativeApp()) return null
  if (!app?.enabled || (!app.url && !app.bazaarUrl)) return null

  return (
    <section className="mx-auto max-w-[1400px] px-4 pb-6 sm:px-6">
      <div className="glass-strong relative overflow-hidden rounded-3xl p-6 sm:p-8">
        <div className="pointer-events-none absolute -left-16 top-0 h-56 w-56 rounded-full bg-aqua/20 blur-[90px]" />
        <div className="relative flex flex-wrap items-center gap-5">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-aqua shadow-glow">
            <Smartphone className="h-7 w-7 text-white" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-extrabold text-white">اپلیکیشن اندروید ارزینو</h2>
            <p className="mt-1 text-xs leading-6 text-slate-400">
              همه امکانات سایت، با قفل اثر انگشت و اعلان قیمت.
              {app.sizeMb > 0 && <> فقط {fa(num(app.sizeMb, 1))} مگابایت.</>}
            </p>
          </div>
          <Link to="/app" className="btn-primary shrink-0 !px-6 !py-3">
            دریافت اپلیکیشن <ArrowLeft className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  )
}
